import FVector from '@data/geometry/FVector';
import polygonClipping from 'polygon-clipping';
import * as THREE from 'three';
import { mergeGeometries, mergeVertices } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { PolygonShapeBuilder } from '../../PolygonShapeBuilder';

export type SlabPolygons = FVector[][][];

export type FloorPerimeters = {
    internalLoops: FVector[][];
    externalLoops?: FVector[][];
};

type SlabMaterials = {
    floor: THREE.Material;
    edges: THREE.LineBasicMaterial;
};

/**
 * Slab component responsible for merging floor perimeters and building the final slab mesh.
 */
export class Slab {
    constructor(
        private readonly baseElevation: number,
        private readonly thickness: number,
        private readonly current: FloorPerimeters,
        private readonly adjacent: FloorPerimeters | undefined,
        private readonly materials: SlabMaterials,
        private readonly withEdges: boolean
    ) {}

    /**
     * Builds the slab mesh from the current floor perimeter(s) and, when provided, the adjacent floor perimeter(s).
     */
    build(): THREE.Group {
        // 1. Compute the correct polygons:
        const currentPolygons = Slab.buildFloorPolygons(this.current.internalLoops, this.current.externalLoops);
        const finalPolygons = this.adjacent
            ? Slab.mergeFloorPolygons(currentPolygons, Slab.buildFloorPolygons(this.adjacent.internalLoops, this.adjacent.externalLoops))
            : currentPolygons;

        // 2. Build the geometry and mesh:
        const geometry = this.createGeometry(finalPolygons);

        const group = new THREE.Group();
        if (!geometry) return group;

        group.add(new THREE.Mesh(geometry, this.materials.floor));
        if (this.withEdges) group.add(Slab.createEdgesFromGeometry(geometry, this.materials.edges));

        return group;
    }

    // =====================================
    // PRIVATE UTILITIES
    // =====================================

    /**
     * Converts the floor loops into the polygon format consumed by ShapeBuilder.
     */
    private static buildFloorPolygons(internalLoops: FVector[][], externalLoops?: FVector[][]): SlabPolygons {
        const internalPolygons = PolygonShapeBuilder.buildPolygonsFromLoops(internalLoops.map((loop) => Slab.closeRing(loop)));
        if (!externalLoops?.length) return internalPolygons;

        const externalPolygons = PolygonShapeBuilder.buildPolygonsFromLoops(externalLoops.map((loop) => Slab.closeRing(loop)));
        return Slab.mergeFloorPolygons(internalPolygons, externalPolygons);
    }

    /**
     * Boolean-unions two multipolygons and falls back to concatenation if clipping fails.
     */
    private static mergeFloorPolygons(left: SlabPolygons, right: SlabPolygons): SlabPolygons {
        if (!left.length) return right;
        if (!right.length) return left;

        try {
            return Slab.fromClipperMultiPolygon(polygonClipping.union(Slab.toClipperMultiPolygon(left), Slab.toClipperMultiPolygon(right)));
        } catch {
            return [...left, ...right];
        }
    }

    /**
     * Extrudes the merged polygons into a slab geometry.
     */
    private createGeometry(polygons: SlabPolygons): THREE.BufferGeometry | null {
        const geometries = PolygonShapeBuilder.createShapesFromPolygons(polygons).map((shape) => {
            const geometry = new THREE.ExtrudeGeometry(shape, {
                depth: this.thickness,
                bevelEnabled: false,
            });
            geometry.translate(0, 0, this.baseElevation);
            return geometry;
        });

        if (!geometries.length) return null;
        return mergeGeometries(geometries, false) ?? geometries[0];
    }

    /**
     * Builds outline edges from the final slab mesh.
     */
    private static createEdgesFromGeometry(geometry: THREE.BufferGeometry, material: THREE.LineBasicMaterial): THREE.LineSegments {
        const weldedGeometry = mergeVertices(geometry.clone(), 1e-5);
        weldedGeometry.computeVertexNormals();
        const edges = new THREE.EdgesGeometry(weldedGeometry, 0.01);
        return new THREE.LineSegments(edges, material);
    }

    /**
     * Ensures every loop is explicitly closed before shape building or boolean operations.
     */
    private static closeRing(perimeter: FVector[]): FVector[] {
        return PolygonShapeBuilder.normalizeLoop(perimeter);
    }

    /**
     * Converts the renderer polygon format into polygon-clipping coordinates.
     */
    private static toClipperMultiPolygon(multiPolygon: SlabPolygons): [number, number][][][] {
        return multiPolygon
            .map((polygon) =>
                polygon.map((ring) => Slab.sanitizeRingForClipper(ring)).filter((ring): ring is [number, number][] => ring !== null)
            )
            .filter((polygon) => polygon.length > 0);
    }

    /**
     * Converts polygon-clipping output back into FVector loops.
     */
    private static fromClipperMultiPolygon(multiPolygon: [number, number][][][]): SlabPolygons {
        return multiPolygon.map((polygon) => polygon.map((ring) => ring.map(([x, y]) => new FVector(x, y, 0))));
    }

    /**
     * Removes duplicate and collinear points so polygon-clipping receives stable rings.
     */
    private static sanitizeRingForClipper(ring: FVector[]): [number, number][] | null {
        const openRing = PolygonShapeBuilder.openLoop(Slab.closeRing(ring));
        if (openRing.length < 3) return null;

        const sanitized: FVector[] = [];
        for (const point of openRing) {
            const snapped = new FVector(Slab.snap(point.x), Slab.snap(point.y), point.z);
            const previous = sanitized[sanitized.length - 1];
            if (previous && previous.x === snapped.x && previous.y === snapped.y) continue;
            sanitized.push(snapped);
        }

        if (sanitized.length < 3) return null;

        const simplified: FVector[] = [];
        for (let i = 0; i < sanitized.length; i++) {
            const previous = sanitized[(i - 1 + sanitized.length) % sanitized.length];
            const current = sanitized[i];
            const next = sanitized[(i + 1) % sanitized.length];
            const cross = (current.x - previous.x) * (next.y - previous.y) - (current.y - previous.y) * (next.x - previous.x);
            if (Math.abs(cross) <= 1e-8) continue;
            simplified.push(current);
        }

        if (simplified.length < 3) return null;

        const closedRing = [...simplified, simplified[0]];
        return closedRing.map((point): [number, number] => [point.x, point.y]);
    }

    /**
     * Snaps coordinates to a fixed precision to reduce tiny floating-point gaps.
     */
    private static snap(value: number): number {
        return Math.round(value * 1e6) / 1e6;
    }
}
